import React, { useState } from 'react';
import { BASE_DOMAIN, autoGenDnsPattern, isBaseDomain } from '../utility/baseDomain';
import Modal from './Modal';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import Select from 'react-select';
import { getSelectStyles } from '../utility/selectStyles';
import axiosInstance from "../utility/axiosInstance";
import { useToast } from "./ToastContext";
import { flavorLabel, isFlavorOptionDisabled } from '../utility/flavorOptions';
import { handleAxiosError } from '../helpers/handleAxiosError';

const HOURS_PER_MONTH = 730;

interface CreateClusterModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreated: () => void;
}

const CreateClusterModal: React.FC<CreateClusterModalProps> = ({ isOpen, onClose, onCreated }) => {
    const { showToast } = useToast();
    const images = useSelector((state: RootState) => state.images.data);
    const flavors = useSelector((state: RootState) => state.flavors.data);
    const networks = useSelector((state: RootState) => state.privateNetwork.networks);
    const keypairs = useSelector((state: RootState) => state.keypairs.data);

    const [name, setName] = useState('');
    const [selectedImage, setSelectedImage] = useState<any>(null);
    const [selectedFlavor, setSelectedFlavor] = useState<any>(null);
    const [selectedNetwork, setSelectedNetwork] = useState<any>(null);
    const [selectedKeypair, setSelectedKeypair] = useState<any>(null);
    const [minSize, setMinSize] = useState(1);
    // HA = min 3 members, so they spread across 3 hosts and a lost member is self-healed.
    const [haMode, setHaMode] = useState(false);
    const [maxSize, setMaxSize] = useState(5);
    const [scaleOutThreshold, setScaleOutThreshold] = useState(70);
    const [scaleInThreshold, setScaleInThreshold] = useState(20);
    const [port, setPort] = useState(80);
    const [dnsName, setDnsName] = useState('');
    const [isDnsGenerated, setIsDnsGenerated] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const FLAVOR_ORDER = ['tiny', 'small', 'medium', 'large', 'xlarge'];

    const generateShortId = (length = 8) => {
        const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        for (let i = 0; i < length; i++) {
            result += chars[Math.floor(Math.random() * chars.length)];
        }
        return result;
    };

    const generateDnsName = () => {
        const base = name ? name.toLowerCase().replace(/[^a-z0-9-]/g, '-') : 'cluster';
        return `${base}-${generateShortId(8)}.${BASE_DOMAIN}`;
    };

    const extractFlavorSize = (flavorName: string): string | null => {
        const n = flavorName.toLowerCase();
        if (n.includes('xlarge')) return 'xlarge';
        if (n.includes('large')) return 'large';
        if (n.includes('medium')) return 'medium';
        if (n.includes('small')) return 'small';
        if (n.includes('tiny')) return 'tiny';
        return null;
    };

    // Determine if selected image is a snapshot (has instance_type_name metadata)
    const selectedImageObj = images.find((img: any) => img.id === selectedImage?.value);
    const instanceTypeName = (selectedImageObj as any)?.metadata?.instance_type_name?.toLowerCase() || '';
    const isFromMediumOrLarger = instanceTypeName.includes('medium') || instanceTypeName.includes('large') || instanceTypeName.includes('xlarge');
    const isUbuntuOrRocky = (() => {
        const imgName = (selectedImageObj?.name || '').toLowerCase();
        const base = ((selectedImageObj as any)?.metadata?.base_image_name || '').toLowerCase();
        return imgName.includes('ubuntu') || base.includes('ubuntu') || imgName.includes('rocky') || base.includes('rocky');
    })();

    const isFlavorRestricted = (flavor: any): boolean => {
        const flavorName = (flavor.name || '').toLowerCase();
        const isTinyOrSmall = flavorName.includes('m1.tiny') || flavorName.includes('m1.small');
        const isRestrictedByMinReq = (selectedImageObj?.min_ram && Number(flavor.ram) < selectedImageObj.min_ram) ||
            (selectedImageObj?.min_disk && Number(flavor.disk) < selectedImageObj.min_disk);
        return ((isUbuntuOrRocky || isFromMediumOrLarger) && isTinyOrSmall) || !!isRestrictedByMinReq;
    };

    const imageOptions = images.map((img: any) => ({ value: img.id, label: img.name }));
    const flavorOptions = flavors
        .filter((fl: any) => !isFlavorRestricted(fl))
        .map((fl: any) => ({
            value: fl.id,
            label: flavorLabel(fl),
            isDisabled: !!fl.restricted,
        }));
    const networkOptions = networks.map(net => ({ value: net.id, label: net.name, subnet_id: net.subnet_id }));
    const keypairOptions = keypairs.map((kp: any) => ({ value: kp.name, label: kp.name }));
    const selectedFlavorData = flavors.find((flavor: any) => String(flavor.id) === String(selectedFlavor?.value));
    const selectedFlavorHourlyRate = Number(selectedFlavorData?.hourly_rate);
    const hasSelectedFlavorRate = Number.isFinite(selectedFlavorHourlyRate) && selectedFlavorHourlyRate >= 0;
    const estimatedMinimumMembers = haMode ? 3 : minSize;
    const estimatedMaximumMembers = Math.max(estimatedMinimumMembers, maxSize);
    const estimatedMinimumHourly = hasSelectedFlavorRate ? selectedFlavorHourlyRate * estimatedMinimumMembers : null;
    const estimatedMaximumHourly = hasSelectedFlavorRate ? selectedFlavorHourlyRate * estimatedMaximumMembers : null;

    const handleSubmit = async () => {
        if (!name || !selectedImage || !selectedFlavor || !selectedNetwork || !selectedKeypair) {
            showToast('Please fill in all required fields', 'error');
            return;
        }
        const fqdnPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
        if (dnsName && isBaseDomain(dnsName) && !autoGenDnsPattern.test(dnsName)) {
            showToast(`DNS name cannot end with .${BASE_DOMAIN}. Leave empty for random generation or click ⟳ Generate.`, 'error');
            return;
        }
        if (dnsName && !isBaseDomain(dnsName) && !fqdnPattern.test(dnsName)) {
            showToast('DNS name is not a valid FQDN. Only alphanumeric characters and hyphens are allowed, separated by dots.', 'error');
            return;
        }

        // Catch the contradiction here rather than three minutes into the build. A cluster is created
        // at one member and ramped, so Heat only rejects max < min part-way through - after the load
        // balancer and the first members exist and are billed.
        if (maxSize < minSize) {
            showToast(`Max instances (${maxSize}) cannot be below min instances (${minSize}).`, 'error');
            return;
        }

        setIsSubmitting(true);
        try {
            await axiosInstance.post('/v1/clusters', {
                name,
                image: selectedImage.value,
                flavor: selectedFlavor.value,
                min_size: minSize,
                max_size: maxSize,
                scale_out_threshold: scaleOutThreshold / 100,
                scale_in_threshold: scaleInThreshold / 100,
                port: port,
                backend_network_id: selectedNetwork.value,
                backend_subnet_id: selectedNetwork.subnet_id,
                key_name: selectedKeypair.value,
                dnsName,
            });
            onClose();
            showToast('Cluster creation started', 'success');

            onCreated();
        } catch (error) {
            console.error("Error creating cluster:", error);
            // Show what the server actually said. A refusal here (a size that needs approval, a
            // quota) carries the reason and the next step; replacing it with "Failed to create
            // cluster" tells the user nothing and they retry the same request.
            showToast(handleAxiosError(error) || 'Failed to create cluster', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Create Cluster" size="md">
            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="create-form">
                <div className="form-group">
                    <label>Cluster Name</label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="my-cluster"
                        autoFocus
                    />
                </div>

                <div className="form-group">
                    <label>Image</label>
                    <Select
                        options={imageOptions}
                        value={selectedImage}
                        onChange={setSelectedImage}
                        placeholder="Select image..."
                        styles={getSelectStyles()}
                    />
                </div>

                <div className="form-group">
                    <label>Flavor</label>
                    <Select
                        options={flavorOptions}
                        isOptionDisabled={isFlavorOptionDisabled as any}
                        value={selectedFlavor && isFlavorRestricted(flavors.find((f: any) => f.id === selectedFlavor?.value) || {}) ? null : selectedFlavor}
                        onChange={(opt) => {
                            setSelectedFlavor(opt);
                        }}
                        placeholder="Select flavor..."
                        styles={getSelectStyles()}
                    />
                    {selectedFlavor && isFlavorRestricted(flavors.find((f: any) => f.id === selectedFlavor?.value) || {}) && (
                        <p style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: '4px' }}>
                            Selected flavor is not compatible with this image. Please choose a larger flavor.
                        </p>
                    )}
                    {selectedFlavor && !isFlavorRestricted(flavors.find((f: any) => f.id === selectedFlavor?.value) || {}) && (
                        <p className="help-text" aria-live="polite" style={{ marginBottom: 0 }}>
                            {hasSelectedFlavorRate && estimatedMinimumHourly !== null && estimatedMaximumHourly !== null ? <>
                                Minimum capacity ({estimatedMinimumMembers} {estimatedMinimumMembers === 1 ? 'member' : 'members'}): <strong>£{estimatedMinimumHourly.toFixed(5)}/hr · ~£{(estimatedMinimumHourly * HOURS_PER_MONTH).toFixed(2)}/mo</strong>.
                                {estimatedMaximumMembers > estimatedMinimumMembers && <> At the maximum of {estimatedMaximumMembers}, it can reach £{estimatedMaximumHourly.toFixed(5)}/hr · ~£{(estimatedMaximumHourly * HOURS_PER_MONTH).toFixed(2)}/mo.</>}
                                {' '}Based on £{selectedFlavorHourlyRate.toFixed(5)}/hr per {selectedFlavorData?.name} member, before credits and usage-based egress.
                            </> : <>Pricing for this flavor is not configured yet, so the console cannot show a reliable cluster estimate.</>}
                        </p>
                    )}
                </div>

                {/* HA is just "min 3", but stating it as a capability matters: a 1- or 2-member cluster
                    is a load-scaling group, not a highly available one. Ticking this sets min 3 so
                    members land on 3 different hosts and a lost member is rebuilt automatically. */}
                <div className="form-group" style={{ border: '1px solid var(--border-strong)', borderRadius: 8, padding: 12 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: haMode ? 10 : 0 }}>
                        <input
                            type="checkbox"
                            checked={haMode}
                            onChange={(e) => {
                                const on = e.target.checked;
                                setHaMode(on);
                                if (on) {
                                    setMinSize(3);
                                    setMaxSize((m) => Math.max(3, m));
                                }
                            }}
                            style={{ width: 16, height: 16 }}
                        />
                        <span style={{ fontWeight: 600 }}>High availability</span>
                        <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(3 members, different hosts, self-healing)</span>
                    </label>
                    {haMode && (
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.86rem', lineHeight: 1.55 }}>
                            Keeps <strong>3 members minimum</strong> on three different physical hosts, so losing one host
                            does not take the app down. A destroyed member is rebuilt automatically within a few minutes,
                            and updates roll one member at a time with little disruption.
                            <br />
                            Your app must <strong>start on every boot</strong> (systemd service or container restart policy),
                            otherwise a replaced member comes back empty and silently leaves the load balancer.
                        </div>
                    )}
                </div>

                <div className="form-group" style={{ display: 'flex', gap: '20px' }}>
                    <div style={{ flex: 1 }}>
                        <label>Min Instances</label>
                        <input
                            type="number"
                            value={minSize}
                            onChange={(e) => {
                                const v = parseInt(e.target.value) || 1;   // parseInt('') is NaN
                                setMinSize(v);
                                if (v < 3) setHaMode(false);   // dropping below 3 is no longer HA
                            }}
                            min={haMode ? 3 : 1}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label>Max Instances</label>
                        <input
                            type="number"
                            value={maxSize}
                            onChange={(e) => setMaxSize(parseInt(e.target.value) || 1)}
                            min={haMode ? 3 : 1}
                        />
                    </div>
                </div>

                <div className="form-group" style={{ display: 'flex', gap: '20px' }}>
                    <div style={{ flex: 1 }}>
                        <label>Scale-Out CPU % <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(default 70)</span></label>
                        <input
                            type="number"
                            value={scaleOutThreshold}
                            onChange={(e) => setScaleOutThreshold(Math.min(100, Math.max(1, parseInt(e.target.value) || 70)))}
                            min="1" max="100"
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label>Scale-In CPU % <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(default 20)</span></label>
                        <input
                            type="number"
                            value={scaleInThreshold}
                            onChange={(e) => setScaleInThreshold(Math.min(99, Math.max(1, parseInt(e.target.value) || 20)))}
                            min="1" max="99"
                        />
                    </div>
                </div>

                <div className="form-group">
                    <label>Web Server Port</label>
                    <input
                        type="number"
                        value={port}
                        onChange={(e) => setPort(parseInt(e.target.value))}
                        min="1"
                        max="65535"
                    />
                </div>

                <div className="form-group">
                    <label>DNS Name (Leave empty for random generation)</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                            type="text"
                            value={dnsName}
                            onChange={(e) => {
                                setDnsName(e.target.value);
                                setIsDnsGenerated(false);
                            }}
                            placeholder="e.g., my-cluster.mydomain.com"
                            style={{ flex: 1 }}
                        />
                        <button
                            type="button"
                            onClick={() => {
                                setDnsName(generateDnsName());
                                setIsDnsGenerated(true);
                            }}
                            className="btn btn-primary"
                            title="Generate a random .redu.cloud domain"
                            style={{ whiteSpace: 'nowrap', color: 'white', height: '40px' }}
                        >
                            ⟳ Generate
                        </button>
                    </div>
                </div>

                <div className="form-group">
                    <label>Network</label>
                    <Select
                        options={networkOptions}
                        value={selectedNetwork}
                        onChange={setSelectedNetwork}
                        placeholder="Select network..."
                        styles={getSelectStyles()}
                    />
                </div>

                <div className="form-group">
                    <label>SSH Keypair</label>
                    <Select
                        options={keypairOptions}
                        value={selectedKeypair}
                        onChange={setSelectedKeypair}
                        placeholder="Select keypair..."
                        styles={getSelectStyles()}
                    />
                </div>

                <div className="modal-actions">
                    <button 
                        type="button"
                        className="cancel-button" 
                        onClick={onClose} 
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button 
                        type="submit"
                        className="submit-button" 
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Launching...' : 'Launch Cluster'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default CreateClusterModal;
